/**
 * Stamina gate (ARCHITECTURE §5 "Stamina & power", owner: movement team). One rule for every dash / feint / spin:
 * the move needs `stamina >= cost`; otherwise it is refused (STAMINA_DENIED, nothing spent) and the caller lets
 * the real move happen instead. STAMINA_EMPTY fires when a spend drops the player below STAMINA.minFeintCost
 * (the moment the HUD starts showing MUST BE REAL).
 */
import { STAMINA } from '@hf/shared';
import { Once, firstThisStep } from '../contact/fx';
import type { SimEventSink } from '../sink';
import type { PlayerSim, SimState } from '../state';

/** Spend `cost` if affordable. Returns false (and emits STAMINA_DENIED) when the move must be real. */
export const trySpend = (s: SimState, ev: SimEventSink, p: PlayerSim, cost: number): boolean => {
  if (p.stamina < cost) {
    if (ev && firstThisStep(Once.STAMINA_DENIED, p.index))
      ev.push({ type: 'STAMINA_DENIED', tick: s.tick, actor: p.index });
    return false;
  }
  const before = p.stamina;
  p.stamina = before - cost;
  if (before >= STAMINA.minFeintCost && p.stamina < STAMINA.minFeintCost && ev)
    ev.push({ type: 'STAMINA_EMPTY', tick: s.tick, actor: p.index });
  return true;
};

/** Live-play regeneration (full refills happen at line-up). */
export const regenStamina = (p: PlayerSim): void => {
  if (p.stamina < STAMINA.max) {
    // Math.min keeps the value a raw double (a `? max : v` merge with the integer max would box it every tick).
    p.stamina = Math.min(p.stamina + STAMINA.regenPerTick, STAMINA.max);
  }
};
